import * as React from 'react';
import { faFileAlt, faUserCircle } from '@fortawesome/free-regular-svg-icons';
import { faPlusCircle, faUsers } from '@fortawesome/free-solid-svg-icons';
import { Card, Col, Row } from 'react-bootstrap';
import { useAuth } from '../../../../Contexts/AuthProvider';
import { useNumericRecord } from '../../../../Hooks/useNumericRecord';
import { IndStatus } from '@api/Entities/Ind/Ind';
import { LeadDispo } from '../../../../../MedicareProAPI/Entities/User/UserLead';
import { PageHeader } from '../../../../Components/PageHeader';
import { StyledModal } from '@app/Components/StyledModal';
import type { StatsCardProps } from '../../../../Components/StatsCard';
import { StatsCardSkeletonGroup, StatsCard } from '../../../../Components/StatsCard';
import { useTranslation } from 'react-i18next';
import { SettingsForm } from './Components/SettingsForm';
import { DashboardCard } from './types';
import type { UserMetaSchemaSimple } from '@api/Repositories/User/UserMetaRepository';
import { UserMetaRepository } from '@api/Repositories/User/UserMetaRepository';
import { IndRepository } from '@api/Repositories/Ind/IndRepository';
import { IndPolicyRepository } from '@api/Repositories/Ind/IndPolicyRepository';
import { useProfileSearch } from '@app/Contexts/ProfileSearchProvider';
import { useToast } from '@app/Contexts/ToastProvider';
import type { UserMeta } from '@api/Entities/User/UserMeta';
import { useRepository } from '@app/Hooks/useRepository';
import { Helmet } from 'react-helmet-async';

type DashboardTotals = {
  totalClients: number;
  totalNonCommissionableClients: number;
  totalProspects: number;
  totalPolicies: number;
  totalLeads: number;
};

/**
 * Agent dashboard page
 */
export const Dashboard = () => {
  const { t } = useTranslation();
  const { openProfileSearch } = useProfileSearch();
  const { notify } = useToast();

  const indRepository = useRepository(IndRepository);
  const indPolicyRepository = useRepository(IndPolicyRepository);
  const userMetaRepository = useRepository(UserMetaRepository);

  const [showPageSettingsModal, setShowPageSettingsModal] = React.useState<boolean>(false);

  const [dashboardCards, setDashboardCards] = React.useState<Array<DashboardCard>>([]);
  const [dashboardCardsLoading, setDashboardCardsLoading] = React.useState<boolean>(true);

  const {
    state: totals,
    loading,
    set,
    setLoading,
  } = useNumericRecord<DashboardTotals>(
    {
      totalClients: 0,
      totalNonCommissionableClients: 0,
      totalProspects: 0,
      totalPolicies: 0,
      totalLeads: 0,
    },
    true
  );

  const session = useAuth();

  const updateDashboardCards = (meta: UserMeta = null) => {
    setDashboardCardsLoading(false);
    setDashboardCards(
      meta
        ? meta.getValue()
        : [
            DashboardCard.Clients,
            DashboardCard.NonCommissionableClients,
            DashboardCard.Policies,
            DashboardCard.Prospects,
            DashboardCard.Leads,
          ]
    );
  };

  React.useEffect(() => {
    if (session.isAuthenticated()) {
      indRepository.getIndsTotal(null, { indStatus: IndStatus.Active }).then((response) => {
        set('totalClients', response.getTotal());
        setLoading('totalClients', false);
      });

      indRepository.getIndsTotal(null, { indStatus: IndStatus.Prospect }).then((response) => {
        set('totalProspects', response.getTotal());
        setLoading('totalProspects', false);
      });

      indRepository
        .getIndsTotal(null, { indStatus: IndStatus.NonCommissionableClient })
        .then((response) => {
          set('totalNonCommissionableClients', response.getTotal());
          setLoading('totalNonCommissionableClients', false);
        });

      indRepository
        .getIndsTotal(null, { leadDismiss: 0, leadDispo: LeadDispo.open })
        .then((response) => {
          set('totalLeads', response.getTotal());
          setLoading('totalLeads', false);
        });

      indPolicyRepository.getPoliciesTotal().then((response) => {
        set('totalPolicies', response.getTotal());
        setLoading('totalPolicies', false);
      });

      userMetaRepository
        .get(session.getUser().getUserId(), 'dashboard.settings.cards')
        .then((meta) => {
          updateDashboardCards(meta);
        })
        .catch((e) => {
          updateDashboardCards(null);
        });
    }
  }, [session.isAuthenticated()]);

  const dashboardCardDetails: Record<DashboardCard, StatsCardProps> = {
    [DashboardCard.Clients]: {
      title: t(`common.${DashboardCard.Clients}`),
      icon: faUserCircle,
      loading: loading.totalClients,
      number: totals.totalClients,
      variant: 'primary',
      onClick: openProfileSearch,
    },
    [DashboardCard.NonCommissionableClients]: {
      title: t(`common.${DashboardCard.NonCommissionableClients}`),
      icon: faUsers,
      loading: loading.totalNonCommissionableClients,
      number: totals.totalNonCommissionableClients,
      variant: 'success',
      progressBar: false,
      onClick: () => {
        notify({
          title: 'Profile Added',
          message: "You've added a new profile Barb Anne; click to view this profile.",
          variant: 'white',
          href: '/agent/leads',
        });
      },
    },
    [DashboardCard.Prospects]: {
      title: t(`common.${DashboardCard.Prospects}`),
      icon: faUsers,
      loading: loading.totalProspects,
      number: totals.totalProspects,
      variant: 'success',
      progressBar: false,
    },
    [DashboardCard.Policies]: {
      title: t(`common.${DashboardCard.Policies}`),
      icon: faFileAlt,
      loading: loading.totalPolicies,
      number: totals.totalPolicies,
      variant: 'info',
    },
    [DashboardCard.Leads]: {
      title: t(`common.${DashboardCard.Leads}`),
      icon: faPlusCircle,
      loading: loading.totalLeads,
      number: totals.totalLeads,
      variant: 'warning',
    },
  };

  return (
    <>
      <Helmet>
        <title>{t('common.dashboard')}</title>
      </Helmet>
      <StyledModal
        show={showPageSettingsModal}
        title="Page Settings"
        onClose={() => setShowPageSettingsModal(false)}
      >
        <SettingsForm
          initialValues={{
            dashboardCards: dashboardCards,
          }}
          onSave={(values) => {
            const userMetaSchema: UserMetaSchemaSimple = {
              key: 'dashboard.settings.cards',
              value: values.dashboardCards,
            };

            return userMetaRepository
              .update(session.getUser().getUserId(), userMetaSchema)
              .then((dashboardCards) => {
                setDashboardCards(values.dashboardCards);

                return dashboardCards;
              });
          }}
        />
      </StyledModal>

      <PageHeader onSettingsClick={() => setShowPageSettingsModal(true)}>
        {t('common.dashboard')}
      </PageHeader>
      {dashboardCardsLoading && (
        <StatsCardSkeletonGroup
          className="mt-1 mb-3"
          items={4}
          style={{
            height: 156,
          }}
        />
      )}
      {dashboardCards && dashboardCardsLoading !== true && (
        <Row xs={dashboardCards.length} className="mt-1 mb-3">
          {dashboardCards.map((card, cardKey) => (
            <Col key={cardKey}>
              <StatsCard {...dashboardCardDetails[card]} />
            </Col>
          ))}
        </Row>
      )}

      <Row className="mt-1">
        <Col className="mb-3">
          <Card className="border bg-white">
            <Card.Body className="px-3">
              <h6 className="fw-bold mt-0 mb-0">{t('common.calendar')}</h6>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};
